import React, { useState, useMemo, useEffect } from 'react';
import './OfferGenerator.css';

// NOTE: This is a lightweight, client-side password gate. It keeps the tool out
// of casual reach (it is not linked anywhere on the marketing site), but it is
// NOT a substitute for real auth. Change the password by setting the
// REACT_APP_OFFER_PASSWORD env var at build time, otherwise it falls back below.
const OFFER_PASSWORD = process.env.REACT_APP_OFFER_PASSWORD || 'truerep2026';
const SESSION_KEY = 'tr_offer_unlocked';

// ----- Helpers ---------------------------------------------------------------

const currency = (n) =>
    isFinite(n)
        ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
        : '$0.00';

const number = (n) => (isFinite(n) ? Math.round(n).toLocaleString('en-US') : '0');

const num = (v) => {
    const n = parseFloat(v);
    return isFinite(n) ? n : 0;
};

// A blank percentage field is treated as 100% so it doesn't zero out ICP views.
const pct = (v) => (v === '' || v === null ? 1 : num(v) / 100);

// CPM from a fee against a given pool of views = fee * 1000 / views.
const cpmFromFee = (fee, views) => (num(views) > 0 ? (num(fee) * 1000) / num(views) : 0);

// Best-effort creator name from a social link (e.g. youtube.com/@MrBeast -> MrBeast).
const creatorFromLink = (url) => {
    if (!url) return '';
    try {
        const u = new URL(url.startsWith('http') ? url : `https://${url}`);
        const at = u.pathname.match(/@([A-Za-z0-9._-]+)/);
        if (at) return at[1];
        const skip = new Set(['c', 'user', 'channel', 'in', 'p', 'reel', 'reels', 'watch', 'shorts', 'tv']);
        const parts = u.pathname.split('/').filter(Boolean);
        const seg = parts.find((p) => !skip.has(p.toLowerCase()));
        if (seg) return decodeURIComponent(seg).replace(/^@/, '');
        return u.hostname.replace(/^www\./, '');
    } catch (e) {
        return '';
    }
};

const blankCampaign = () => ({
    id: Math.random().toString(36).slice(2),
    socialLink: '',
    campaignType: '',
    expectedViews: '',
    minViews: '',
    maxViews: '',
    geoPct: '',
    agePct: '',
    genderPct: '',
    cost: '',
    count: '1',
    maxIcpCpm: '',
    // User-editable fees. Empty string => follow the cost-of-campaign default.
    feeMin: '',
    feeExpected: '',
    feeMax: '',
});

// ----- Password gate ---------------------------------------------------------

const PasswordGate = ({ onUnlock }) => {
    const [value, setValue] = useState('');
    const [error, setError] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        if (value === OFFER_PASSWORD) {
            sessionStorage.setItem(SESSION_KEY, '1');
            onUnlock();
        } else {
            setError(true);
        }
    };

    return (
        <div className="og-gate">
            <form className="og-gate-card" onSubmit={submit}>
                <h1>Offer Generator</h1>
                <p>This tool is restricted. Enter the password to continue.</p>
                <input
                    type="password"
                    autoFocus
                    placeholder="Password"
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setError(false);
                    }}
                />
                {error && <span className="og-gate-error">Incorrect password. Try again.</span>}
                <button type="submit">Unlock</button>
            </form>
        </div>
    );
};

// ----- Small field component -------------------------------------------------

const Field = ({ label, suffix, ...props }) => (
    <label className="og-field">
        <span>{label}</span>
        <div className="og-input-wrap">
            <input {...props} />
            {suffix && <span className="og-suffix">{suffix}</span>}
        </div>
    </label>
);

// A view-tier row: editable fee (defaults to cost of campaign) and the resulting
// ICP CPM, color-coded against the Max ICP CPM.
const FeeRow = ({ tier, icpViews, fee, autoFee, maxCpm, onFee }) => {
    const effectiveFee = fee === '' ? autoFee : num(fee);
    const cpm = cpmFromFee(effectiveFee, icpViews);
    const over = cpm > num(maxCpm);
    return (
        <div className="og-fee-row">
            <span className="og-fee-tier">{tier}</span>
            <span className="og-fee-views">{number(icpViews)} ICP views</span>
            <div className="og-input-wrap og-fee-input">
                <span className="og-prefix">$</span>
                <input
                    type="number"
                    min="0"
                    step="any"
                    value={fee === '' ? '' : fee}
                    placeholder={autoFee.toFixed(2)}
                    onChange={(e) => onFee(e.target.value)}
                />
            </div>
            <span
                className={`og-cpm ${num(maxCpm) <= 0 ? '' : over ? 'og-cpm-over' : 'og-cpm-under'}`}
                title={over ? 'Over Max ICP CPM' : 'At or under Max ICP CPM'}
            >
                {currency(cpm)} CPM
            </span>
        </div>
    );
};

// ----- Campaign card ---------------------------------------------------------

const CampaignCard = ({ index, c, onChange, onRemove, computed }) => {
    const set = (field) => (e) => onChange(c.id, field, e.target.value);

    return (
        <div className="og-card">
            <div className="og-card-head">
                <h3>Campaign {index + 1}</h3>
                <button className="og-remove" onClick={() => onRemove(c.id)} title="Remove campaign" aria-label="Remove campaign">
                    <span aria-hidden="true">×</span>
                </button>
            </div>

            <div className="og-grid og-grid-2">
                <Field
                    label="Social link (YouTube, Instagram, etc.)"
                    type="url"
                    placeholder="https://youtube.com/@channel"
                    value={c.socialLink}
                    onChange={set('socialLink')}
                />
                <Field
                    label="Campaign type"
                    type="text"
                    placeholder="e.g. Dedicated video, 60s integration"
                    value={c.campaignType}
                    onChange={set('campaignType')}
                />
            </div>

            <div className="og-grid">
                <Field label="Expected views" type="number" min="0" value={c.expectedViews} onChange={set('expectedViews')} />
                <Field label="Min views" type="number" min="0" value={c.minViews} onChange={set('minViews')} />
                <Field label="Max views" type="number" min="0" value={c.maxViews} onChange={set('maxViews')} />
            </div>

            <div className="og-grid">
                <Field label="Geo % in ICP" type="number" min="0" max="100" suffix="%" value={c.geoPct} onChange={set('geoPct')} />
                <Field label="Age % in ICP" type="number" min="0" max="100" suffix="%" value={c.agePct} onChange={set('agePct')} />
                <Field label="Gender % in ICP" type="number" min="0" max="100" suffix="%" value={c.genderPct} onChange={set('genderPct')} />
            </div>

            <div className="og-grid">
                <Field label="Cost of campaign" type="number" min="0" suffix="$" value={c.cost} onChange={set('cost')} />
                <Field label="Count of campaigns" type="number" min="1" value={c.count} onChange={set('count')} />
                <Field label="Max ICP CPM" type="number" min="0" step="any" suffix="$" value={c.maxIcpCpm} onChange={set('maxIcpCpm')} />
            </div>

            <div className="og-fees">
                <div className="og-fees-head">
                    <span>Max ICP fee</span>
                    <span className="og-fees-note">Defaults to cost of campaign · CPM turns red over Max ICP CPM</span>
                </div>
                <FeeRow tier="Min" icpViews={computed.icpMin} fee={c.feeMin} autoFee={computed.autoFee} maxCpm={c.maxIcpCpm} onFee={(v) => onChange(c.id, 'feeMin', v)} />
                <FeeRow tier="Expected" icpViews={computed.icpExpected} fee={c.feeExpected} autoFee={computed.autoFee} maxCpm={c.maxIcpCpm} onFee={(v) => onChange(c.id, 'feeExpected', v)} />
                <FeeRow tier="Max" icpViews={computed.icpMax} fee={c.feeMax} autoFee={computed.autoFee} maxCpm={c.maxIcpCpm} onFee={(v) => onChange(c.id, 'feeMax', v)} />
            </div>

            <div className="og-card-totals">
                <div>
                    <span>Fee (cost of campaign)</span>
                    <strong>{currency(computed.autoFee)}</strong>
                </div>
                <div>
                    <span>× {computed.count} campaign(s)</span>
                    <strong>{currency(computed.totalCost)}</strong>
                </div>
            </div>
        </div>
    );
};

// ----- Main page -------------------------------------------------------------

export const OfferGenerator = () => {
    const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
    const [campaigns, setCampaigns] = useState([blankCampaign()]);
    const [copied, setCopied] = useState(false);
    const [tableCopied, setTableCopied] = useState(false);
    const [includeAll, setIncludeAll] = useState(false);

    useEffect(() => {
        document.title = 'Offer Generator · TrueRep';
    }, []);

    const onChange = (id, field, value) =>
        setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

    const addCampaign = () => setCampaigns((prev) => [...prev, blankCampaign()]);
    const removeCampaign = (id) =>
        setCampaigns((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : prev));

    // Per-campaign computed numbers.
    const computedByCampaign = useMemo(
        () =>
            campaigns.map((c) => {
                const count = num(c.count) || 1;
                const cost = num(c.cost); // cost of campaign = the controlling fee
                const icpFactor = pct(c.geoPct) * pct(c.agePct) * pct(c.genderPct);

                return {
                    count,
                    autoFee: cost,
                    // ICP-qualified views = views * Geo% * Age% * Gender%
                    icpMin: num(c.minViews) * icpFactor,
                    icpExpected: num(c.expectedViews) * icpFactor,
                    icpMax: num(c.maxViews) * icpFactor,
                    // Raw views (for total CPM) and totals roll up by count.
                    rawExpected: num(c.expectedViews) * count,
                    icpExpectedTotal: num(c.expectedViews) * icpFactor * count,
                    totalCost: cost * count,
                };
            }),
        [campaigns]
    );

    // Grand totals across all campaign cards.
    const totals = useMemo(
        () =>
            computedByCampaign.reduce(
                (acc, c) => ({
                    cost: acc.cost + c.totalCost,
                    campaigns: acc.campaigns + c.count,
                    rawViews: acc.rawViews + c.rawExpected,
                    icpViews: acc.icpViews + c.icpExpectedTotal,
                }),
                { cost: 0, campaigns: 0, rawViews: 0, icpViews: 0 }
            ),
        [computedByCampaign]
    );

    const totalCpm = totals.rawViews > 0 ? (totals.cost * 1000) / totals.rawViews : 0;
    const icpCpm = totals.icpViews > 0 ? (totals.cost * 1000) / totals.icpViews : 0;

    // Build the shareable, email-ready offer text — external-party facing only.
    const offerText = useMemo(() => {
        const lines = [];
        const creators = [...new Set(campaigns.map((c) => creatorFromLink(c.socialLink)).filter(Boolean))];
        const heading = creators.length ? `Proposed Campaign Offer — ${creators.join(', ')}` : 'Proposed Campaign Offer';
        lines.push(heading);
        lines.push('='.repeat(Math.max(40, heading.length)));
        lines.push('');

        campaigns.forEach((c, i) => {
            const comp = computedByCampaign[i];
            lines.push(`Deliverable ${i + 1}${c.socialLink ? ` — ${c.socialLink}` : ''}`);
            if (c.campaignType) lines.push(`  Campaign type: ${c.campaignType}`);
            lines.push(`  Number of campaigns: ${comp.count}`);
            lines.push(`  Fee per campaign: ${currency(comp.autoFee)}`);
            lines.push(`  Fee total (×${comp.count}): ${currency(comp.totalCost)}`);
            lines.push('');
        });

        lines.push('-'.repeat(40));
        lines.push(`Total campaigns: ${totals.campaigns}`);
        lines.push(`Consolidated offer: ${currency(totals.cost)}`);
        lines.push('');
        return lines.join('\n');
    }, [campaigns, computedByCampaign, totals]);

    // Tab-separated table — pastes as a real table into Sheets/Excel/email.
    // "Include all metrics" appends every inputted and calculated field.
    const tableText = useMemo(() => {
        const tnum = (n) => (isFinite(n) ? Math.round(n) : 0);
        const tmoney = (n) => (isFinite(n) ? Math.round(n * 100) / 100 : 0);
        // Percentages export as their decimal form (e.g. 80 -> 0.8); blanks stay blank.
        const tpct = (v) => (v === '' || v === null ? '' : num(v) / 100);

        const headBase = ['Creator', 'Link', 'Campaign Type', '# Campaigns', 'Fee / Campaign', 'Fee Total'];
        const headAll = [
            'Expected Views', 'Min Views', 'Max Views',
            'Geo % ICP', 'Age % ICP', 'Gender % ICP',
            'ICP Expected Views', 'ICP Min Views', 'ICP Max Views',
            'Cost of Campaign', 'Max ICP CPM', 'Total CPM', 'ICP CPM',
        ];
        const header = includeAll ? [...headBase, ...headAll] : headBase;
        const rows = [header.join('\t')];

        campaigns.forEach((c, i) => {
            const comp = computedByCampaign[i];
            const base = [
                creatorFromLink(c.socialLink),
                c.socialLink,
                c.campaignType,
                comp.count,
                tmoney(comp.autoFee),
                tmoney(comp.totalCost),
            ];
            let extra = [];
            if (includeAll) {
                const singleTotalCpm = num(c.expectedViews) > 0 ? (num(c.cost) * 1000) / num(c.expectedViews) : 0;
                const singleIcpCpm = comp.icpExpected > 0 ? (num(c.cost) * 1000) / comp.icpExpected : 0;
                extra = [
                    tnum(num(c.expectedViews)), tnum(num(c.minViews)), tnum(num(c.maxViews)),
                    tpct(c.geoPct), tpct(c.agePct), tpct(c.genderPct),
                    tnum(comp.icpExpected), tnum(comp.icpMin), tnum(comp.icpMax),
                    tmoney(num(c.cost)), c.maxIcpCpm, tmoney(singleTotalCpm), tmoney(singleIcpCpm),
                ];
            }
            rows.push([...base, ...extra].join('\t'));
        });

        const totalsBase = ['TOTAL', '', '', totals.campaigns, '', tmoney(totals.cost)];
        const totalsExtra = includeAll
            ? [tnum(totals.rawViews), '', '', '', '', '', tnum(totals.icpViews), '', '', '', '', tmoney(totalCpm), tmoney(icpCpm)]
            : [];
        rows.push([...totalsBase, ...totalsExtra].join('\t'));

        return rows.join('\n');
    }, [campaigns, computedByCampaign, totals, totalCpm, icpCpm, includeAll]);

    const copyText = async (text, setFlag) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (e) {
            // Fallback for older browsers / insecure contexts.
            const ta = document.createElement('textarea');
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        setFlag(true);
        setTimeout(() => setFlag(false), 2000);
    };

    const copyOffer = () => copyText(offerText, setCopied);
    const copyTable = () => copyText(tableText, setTableCopied);

    if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

    return (
        <div className="og">
            {/* Summary / final offer pinned at the top */}
            <div className="og-summary">
                <div className="og-summary-head">
                    <h1>Offer Generator</h1>
                    <span className="og-summary-sub">{totals.campaigns} campaign(s) across {campaigns.length} deliverable(s)</span>
                </div>
                <div className="og-summary-grid">
                    <div className="og-summary-tile og-summary-primary">
                        <span>Total campaign cost</span>
                        <strong>{currency(totals.cost)}</strong>
                    </div>
                    <div className="og-summary-tile">
                        <span>Quantity</span>
                        <strong>{number(totals.campaigns)}</strong>
                    </div>
                    <div className="og-summary-tile">
                        <span>Total CPM</span>
                        <strong>{currency(totalCpm)}</strong>
                    </div>
                    <div className="og-summary-tile">
                        <span>ICP CPM</span>
                        <strong>{currency(icpCpm)}</strong>
                    </div>
                </div>
            </div>

            <div className="og-body">
                <div className="og-cards">
                    {campaigns.map((c, i) => (
                        <CampaignCard
                            key={c.id}
                            index={i}
                            c={c}
                            computed={computedByCampaign[i]}
                            onChange={onChange}
                            onRemove={removeCampaign}
                        />
                    ))}
                    <button className="og-add" onClick={addCampaign}>
                        + Add campaign
                    </button>
                </div>

                <div className="og-share">
                    <div className="og-share-head">
                        <h2>Shareable offer</h2>
                        <button className="og-copy" onClick={copyOffer}>
                            {copied ? 'Copied!' : 'Copy for email'}
                        </button>
                    </div>
                    <pre className="og-share-text">{offerText}</pre>

                    <div className="og-share-head og-table-head">
                        <h2>Table format</h2>
                        <button className="og-copy" onClick={copyTable}>
                            {tableCopied ? 'Copied!' : 'Copy table'}
                        </button>
                    </div>
                    <label className="og-toggle">
                        <input
                            type="checkbox"
                            checked={includeAll}
                            onChange={(e) => setIncludeAll(e.target.checked)}
                        />
                        <span>Include all metrics (every inputted &amp; calculated field)</span>
                    </label>
                    <pre className="og-share-text og-table-text">{tableText}</pre>
                </div>
            </div>
        </div>
    );
};

export default OfferGenerator;
